import type {
  Collection,
  CollectionOptionGroup,
  OptionRef,
  UserSelections,
} from '@/types/collection';

/**
 * Состояние выбора в мастере.
 *
 * Чистые функции без React: то же состояние собирается в тестах, а экран
 * остаётся тонким. Значения групп — всегда массивы, даже для одиночного
 * выбора: ровно та форма, которую ждёт сервер (см. UserSelections).
 */
export interface WizardSelections {
  groups: Record<string, string[]>;
  aspectRatioId: string;
}

export function initialSelections(collection: Collection): WizardSelections {
  const groups: Record<string, string[]> = {};

  for (const group of collection.optionGroups) {
    groups[group.id] = [];
  }

  return { groups, aspectRatioId: collection.generation.defaultAspectRatioId };
}

/**
 * Начальное состояние мастера с уже подставленными значениями.
 *
 * Каждая обязательная группа получает первый вариант, который не блокирован
 * уже выбранным в более ранней группе (тот же алгоритм совместимости, что
 * у presetSelections) — сводка не выглядит пустой сразу после открытия.
 * Необязательные группы остаются пустыми: додумывать декоративные детали,
 * которые человек не выбирал, не дело мастера.
 */
export function defaultSelections(collection: Collection): WizardSelections {
  let state = initialSelections(collection);
  const groups = [...collection.optionGroups].sort((left, right) => left.order - right.order);

  for (const group of groups) {
    if (!group.required) {
      continue;
    }

    // Та же формула, что у missingGroups: сколько вариантов нужно набрать.
    const need = group.selection === 'multiple' ? (group.min ?? 1) : 1;
    let picked = 0;

    for (const option of group.options) {
      if (picked >= need) {
        break;
      }

      if (optionBlockReason(collection, state, group.id, option.id)) {
        continue;
      }

      state = toggleOption(collection, state, group.id, option.id);
      picked += 1;
    }
  }

  return state;
}

/** Выбор прошлой работы, принесённый кнопкой «Повторить». */
export interface SelectionPreset {
  groups: Record<string, string[]>;
  aspectRatioId?: string | undefined;
}

/**
 * Начальное состояние мастера по прошлой работе.
 *
 * Пресет — предложение, а не приказ: коллекция версионируется, и вариант из
 * прошлой генерации мог исчезнуть, переехать за предел max или стать
 * несочетаемым с другим. Всё негодное молча отбрасывается — мастер должен
 * открыться в состоянии, из которого можно отправить запрос, а не в
 * состоянии, которое сервер отклонит.
 */
export function presetSelections(
  collection: Collection,
  preset: SelectionPreset,
): WizardSelections {
  let state = initialSelections(collection);

  // Порядок групп важен: выбор в одной группе может закрыть вариант в другой.
  const groups = [...collection.optionGroups].sort((left, right) => left.order - right.order);

  for (const group of groups) {
    for (const optionId of preset.groups[group.id] ?? []) {
      const exists = group.options.some((option) => option.id === optionId);
      const blocked = optionBlockReason(collection, state, group.id, optionId);

      if (exists && !blocked) {
        state = toggleOption(collection, state, group.id, optionId);
      }
    }
  }

  const ratioExists = collection.generation.aspectRatios.some(
    (ratio) => ratio.id === preset.aspectRatioId,
  );

  return ratioExists && preset.aspectRatioId
    ? { ...state, aspectRatioId: preset.aspectRatioId }
    : state;
}

export function toggleOption(
  collection: Collection,
  selections: WizardSelections,
  groupId: string,
  optionId: string,
): WizardSelections {
  const group = collection.optionGroups.find((candidate) => candidate.id === groupId);

  if (!group) {
    return selections;
  }

  const current = selections.groups[groupId] ?? [];

  /*
   * Обязательная группа одиночного выбора не может опустеть повторным
   * нажатием на уже выбранный вариант: с подстановкой значений по
   * умолчанию (defaultSelections) в ней почти всегда что-то уже выбрано,
   * и повторное нажатие естественно читается как «оставь как есть», а не
   * как «сними выбор». У необязательных групп пустое состояние законно —
   * там снятие выбора сохраняется.
   */
  if (group.selection === 'single' && group.required && current.includes(optionId)) {
    return selections;
  }

  const next = current.includes(optionId)
    ? current.filter((id) => id !== optionId)
    : nextWithOption(group, current, optionId);

  return { ...selections, groups: { ...selections.groups, [groupId]: next } };
}

function nextWithOption(
  group: CollectionOptionGroup,
  current: string[],
  optionId: string,
): string[] {
  if (group.selection === 'single') {
    return [optionId];
  }

  // Переполнение группы — не ошибка пользователя, а нажатие, которое нечему
  // выполнить: карточка сверх максимума показывается заблокированной.
  if (group.max !== undefined && current.length >= group.max) {
    return current;
  }

  return [...current, optionId];
}

/**
 * Почему вариант нельзя выбрать прямо сейчас, или null.
 *
 * Уже выбранный вариант не блокируется никогда: иначе конфликт становится
 * ловушкой — снять его было бы нечем.
 */
export function optionBlockReason(
  collection: Collection,
  selections: WizardSelections,
  groupId: string,
  optionId: string,
): string | null {
  const current = selections.groups[groupId] ?? [];

  if (current.includes(optionId)) {
    return null;
  }

  const chosen = chosenKeys(selections);

  for (const rule of collection.compatibility) {
    if (rule.kind !== 'forbid' || !chosen.has(refKey(rule.when))) {
      continue;
    }
    if (rule.targets.some((target) => target.group === groupId && target.option === optionId)) {
      return rule.reason;
    }
  }

  const group = collection.optionGroups.find((candidate) => candidate.id === groupId);

  if (group?.max !== undefined && current.length >= group.max) {
    return `Больше ${group.max} вариантов выбрать нельзя.`;
  }

  return null;
}

/** Обязательные группы, в которых ещё нечего отправлять. */
export function missingGroups(
  collection: Collection,
  selections: WizardSelections,
): CollectionOptionGroup[] {
  return collection.optionGroups.filter((group) => {
    if (!group.required) {
      return false;
    }

    const selected = selections.groups[group.id] ?? [];
    const min = group.selection === 'multiple' ? (group.min ?? 1) : 1;

    return selected.length < min;
  });
}

/**
 * Нарушенные правила коллекции.
 *
 * Блокировка карточек не покрывает случай, когда конфликт возникает от смены
 * другой группы: детали можно выбрать до состава. Повторение серверной
 * проверки на клиенте нужно, чтобы человек увидел причину до отправки —
 * решает всё равно сервер.
 */
export function selectionIssues(collection: Collection, selections: WizardSelections): string[] {
  const chosen = chosenKeys(selections);
  const issues: string[] = [];

  for (const rule of collection.compatibility) {
    if (!chosen.has(refKey(rule.when))) {
      continue;
    }

    const matched = rule.targets.some((target) => chosen.has(refKey(target)));

    if (rule.kind === 'forbid' ? matched : !matched) {
      issues.push(rule.reason);
    }
  }

  return issues;
}

export function toUserSelections(
  collection: Collection,
  selections: WizardSelections,
  photoshootSize: number,
): UserSelections {
  return {
    collectionId: collection.id,
    collectionVersion: collection.version,
    groups: selections.groups,
    aspectRatioId: selections.aspectRatioId,
    photoshootSize,
  };
}

function chosenKeys(selections: WizardSelections): Set<string> {
  return new Set(
    Object.entries(selections.groups).flatMap(([group, optionIds]) =>
      optionIds.map((option) => refKey({ group, option })),
    ),
  );
}

function refKey(ref: OptionRef): string {
  return `${ref.group}::${ref.option}`;
}
